import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import fetchRoutes, { ApiResponse, fetchSplitRoutes, SplitWorksheetData, fetchComboRoutes, RouteComboResponse, fetchViewPayload, RoutePayload, fetchReplay, isRouteFlagEnabled } from '../services/RouteService';
import { authProvider } from '../utils/authProvider';

interface SplitWorksheetFetchOptions {
    dcName?: string;
    fromDate?: string;
    toDate?: string;
}

interface UseRoutingStateReturn {
    // Tab state
    value: number;
    setValue: (value: number) => void;

    // Route data state
    apiData: ApiResponse | null;
    loading: boolean;
    error: string | null;

    // Split worksheet state
    splitWorksheetApiData: SplitWorksheetData[];
    splitWorksheetLoading: boolean;
    splitWorksheetError: string | null;

    // Combo routes state
    comboData: Record<number, RouteComboResponse>;
    comboLoading: Record<number, boolean>;
    comboError: Record<number, string | null>;

    // View payload state
    viewPayloadData: RoutePayload | null;
    viewPayloadLoading: boolean;
    viewPayloadError: string | null;
    viewPayloadDialogOpen: boolean;
    setViewPayloadDialogOpen: (open: boolean) => void;
    currentRouteId: string | null;
    setCurrentRouteId: (id: string | null) => void;
    jsonSearchTerm: string;
    setJsonSearchTerm: (term: string) => void;
    expandedPayloadRows: Set<number>;
    setExpandedPayloadRows: React.Dispatch<React.SetStateAction<Set<number>>>;

    // Replay state
    replayData: any;
    replayLoading: boolean;
    replayError: string | null;
    replayConfirmDialogOpen: boolean;
    setReplayConfirmDialogOpen: (open: boolean) => void;
    pendingReplayRouteId: number | null;
    setPendingReplayRouteId: (id: number | null) => void;

    // Filters and pagination
    filters: Record<string, string>;
    setFilters: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    dateFilters: { from: string; to: string };
    setDateFilters: React.Dispatch<React.SetStateAction<{ from: string; to: string }>>;
    splitWorksheetFilters: Record<string, string>;
    setSplitWorksheetFilters: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    page: number;
    setPage: (page: number) => void;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;

    // Expanded rows
    expandedRows: Set<number>;
    setExpandedRows: React.Dispatch<React.SetStateAction<Set<number>>>;

    // Computed values
    filteredAndSortedData: any[];
    paginatedData: any[];
    filteredAndSortedSplitWorksheetData: SplitWorksheetData[];
    totalPages: number;
    totalElements: number;
    startEntry: number;
    endEntry: number;

    // Handlers
    handleChange: (event: React.SyntheticEvent, newValue: number) => void;
    handleFilterChange: (column: string, value: string) => void;
    handleDateFilterChange: (type: 'from' | 'to', value: string) => void;
    handleClearDateFilters: () => void;
    handleRowsPerPageChange: (rowsPerPage: number) => void;
    handlePageChange: (newPage: number) => void;
    handleToggleExpand: (rowId: number) => void;

    // API functions
    fetchRouteData: () => Promise<void>;
    fetchSplitWorksheetData: (options?: SplitWorksheetFetchOptions) => Promise<void>;
    fetchComboData: (rowId: number, createTs: string, isSplit: boolean, routeId: string) => Promise<void>;
    fetchViewPayloadData: (rowId: number, routeId: string) => Promise<void>;
    fetchReplayData: (routeId: number) => Promise<void>;
}

export const useRoutingState = (selectedDC: string = "WSLC"): UseRoutingStateReturn => {
    // Tab state
    const [value, setValue] = useState(0);

    // Route data state
    const [apiData, setApiData] = useState<ApiResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const routeAbortControllerRef = useRef<AbortController | null>(null);
    const latestRouteRequestIdRef = useRef(0);
    const activeRouteRequestKeyRef = useRef<string | null>(null);

    // Split worksheet state
    const [splitWorksheetApiData, setSplitWorksheetApiData] = useState<SplitWorksheetData[]>([]);
    const [splitWorksheetLoading, setSplitWorksheetLoading] = useState(false);
    const [splitWorksheetError, setSplitWorksheetError] = useState<string | null>(null);
    const splitAbortControllerRef = useRef<AbortController | null>(null);
    const latestSplitRequestIdRef = useRef(0);
    const activeSplitRequestKeyRef = useRef<string | null>(null);

    // Combo routes state
    const [comboData, setComboData] = useState<Record<number, RouteComboResponse>>({});
    const [comboLoading, setComboLoading] = useState<Record<number, boolean>>({});
    const [comboError, setComboError] = useState<Record<number, string | null>>({});
    const comboAbortControllersRef = useRef<Record<number, AbortController | null>>({});
    const latestComboRequestIdsRef = useRef<Record<number, number>>({});
    const activeComboRequestKeysRef = useRef<Record<number, string | null>>({});

    // View payload state
    const [viewPayloadData, setViewPayloadData] = useState<RoutePayload | null>(null);
    const [viewPayloadLoading, setViewPayloadLoading] = useState(false);
    const [viewPayloadError, setViewPayloadError] = useState<string | null>(null);
    const [viewPayloadDialogOpen, setViewPayloadDialogOpen] = useState(false);
    const [currentRouteId, setCurrentRouteId] = useState<string | null>(null);
    const [jsonSearchTerm, setJsonSearchTerm] = useState('');
    const [expandedPayloadRows, setExpandedPayloadRows] = useState<Set<number>>(new Set());

    // Replay state
    const [replayConfirmDialogOpen, setReplayConfirmDialogOpen] = useState(false);
    const [pendingReplayRouteId, setPendingReplayRouteId] = useState<number | null>(null);
    const [replayData, setReplayData] = useState<any>(null);
    const [replayLoading, setReplayLoading] = useState(false);
    const [replayError, setReplayError] = useState<string | null>(null);

    // Filters and pagination
    const [filters, setFilters] = useState<Record<string, string>>({ dcName: selectedDC });
    const [dateFilters, setDateFilters] = useState<{ from: string; to: string }>({ from: '', to: '' });
    const [splitWorksheetFilters, setSplitWorksheetFilters] = useState<Record<string, string>>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // Expanded rows
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    // API fetch functions
    const fetchRouteData = useCallback(async () => {
        let requestController: AbortController | null = null;
        let requestId: number | null = null;
        let requestKey: string | null = null;

        // Don't fetch if selectedDC is empty
        if (!selectedDC) {
            routeAbortControllerRef.current?.abort();
            activeRouteRequestKeyRef.current = null;
            setLoading(false);
            return;
        }

        try {
            const requestBody: any = {
                dcName: selectedDC,
                page: 0,
                size: 1000
            };

            // Conditionally add startDate and endDate if filters are applied
            if (dateFilters.from) {
                requestBody.startDate = dateFilters.from;
            }
            if (dateFilters.to) {
                requestBody.endDate = dateFilters.to;
            }

            // Conditionally add fileName, routeId, and status if filters are applied
            if (filters.filename && filters.filename.trim() !== '') {
                requestBody.fileName = filters.filename;
            }
            if (filters.route && filters.route.trim() !== '') {
                requestBody.routeId = filters.route;
            }
            if (filters.status && filters.status.trim() !== '') {
                requestBody.status = filters.status;
            }

            requestKey = JSON.stringify(requestBody);
            if (activeRouteRequestKeyRef.current === requestKey) {
                return;
            }

            routeAbortControllerRef.current?.abort();
            requestController = new AbortController();
            routeAbortControllerRef.current = requestController;
            requestId = ++latestRouteRequestIdRef.current;
            activeRouteRequestKeyRef.current = requestKey;

            setLoading(true);
            setError(null);

            const data: ApiResponse = await fetchRoutes(requestBody, requestController.signal);

            if (requestId !== latestRouteRequestIdRef.current) {
                return;
            }

            setApiData(data);
        } catch (err) {
            if ((err as any)?.code === 'ERR_CANCELED' || (err as any)?.name === 'CanceledError') {
                return;
            }

            if (requestId !== null && requestId !== latestRouteRequestIdRef.current) {
                return;
            }

            setError(err instanceof Error ? err.message : 'An error occurred while fetching data');
            console.error('Error fetching route data:', err);
        } finally {
            if (routeAbortControllerRef.current === requestController) {
                routeAbortControllerRef.current = null;
                if (activeRouteRequestKeyRef.current === requestKey) {
                    activeRouteRequestKeyRef.current = null;
                }
            }

            if (requestId !== null && requestId === latestRouteRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [selectedDC, dateFilters.from, dateFilters.to, filters.filename, filters.route, filters.status]);

    const fetchSplitWorksheetData = useCallback(async (options?: SplitWorksheetFetchOptions) => {
        let requestController: AbortController | null = null;
        let requestId: number | null = null;
        let splitRequestKey: string | null = null;
        const targetDcName = options?.dcName || selectedDC;

        // Don't fetch if selectedDC is empty
        if (!targetDcName) {
            splitAbortControllerRef.current?.abort();
            activeSplitRequestKeyRef.current = null;
            setSplitWorksheetLoading(false);
            return;
        }

        try {
            const request = {
                dcName: targetDcName,
                fromDate: options?.fromDate,
                toDate: options?.toDate
            };

            splitRequestKey = JSON.stringify(request);
            if (activeSplitRequestKeyRef.current === splitRequestKey) {
                return;
            }

            splitAbortControllerRef.current?.abort();
            requestController = new AbortController();
            splitAbortControllerRef.current = requestController;
            requestId = ++latestSplitRequestIdRef.current;
            activeSplitRequestKeyRef.current = splitRequestKey;

            setSplitWorksheetLoading(true);
            setSplitWorksheetError(null);

            const data = await fetchSplitRoutes(request, requestController.signal);

            if (requestId !== latestSplitRequestIdRef.current) {
                return;
            }

            setSplitWorksheetApiData(data);
        } catch (err) {
            if ((err as any)?.code === 'ERR_CANCELED' || (err as any)?.name === 'CanceledError') {
                return;
            }

            if (requestId !== null && requestId !== latestSplitRequestIdRef.current) {
                return;
            }

            setSplitWorksheetError(err instanceof Error ? err.message : 'An error occurred while fetching split worksheet data');
            console.error('Error fetching split worksheet data:', err);
        } finally {
            if (splitAbortControllerRef.current === requestController) {
                splitAbortControllerRef.current = null;
                if (activeSplitRequestKeyRef.current === splitRequestKey) {
                    activeSplitRequestKeyRef.current = null;
                }
            }

            if (requestId !== null && requestId === latestSplitRequestIdRef.current) {
                setSplitWorksheetLoading(false);
            }
        }
    }, [selectedDC]);

    const fetchComboData = useCallback(async (rowId: number, createTs: string, isSplit: boolean, routeId: string) => {
        const requestKey = JSON.stringify({ rowId, selectedDC, createTs, isSplit, routeId });
        if (activeComboRequestKeysRef.current[rowId] === requestKey) {
            return;
        }

        comboAbortControllersRef.current[rowId]?.abort();

        const requestController = new AbortController();
        comboAbortControllersRef.current[rowId] = requestController;
        const requestId = (latestComboRequestIdsRef.current[rowId] || 0) + 1;
        latestComboRequestIdsRef.current[rowId] = requestId;
        activeComboRequestKeysRef.current[rowId] = requestKey;

        setComboLoading(prev => ({ ...prev, [rowId]: true }));
        setComboError(prev => ({ ...prev, [rowId]: null }));

        try {
            const data: RouteComboResponse = await fetchComboRoutes(rowId, selectedDC, createTs, isSplit, routeId);

            if (requestId !== latestComboRequestIdsRef.current[rowId]) {
                return;
            }

            setComboData(prev => ({
                ...prev,
                [rowId]: data
            }));
        } catch (err) {
            if ((err as any)?.code === 'ERR_CANCELED' || (err as any)?.name === 'CanceledError') {
                return;
            }

            if (requestId !== latestComboRequestIdsRef.current[rowId]) {
                return;
            }

            setComboError(prev => ({ ...prev, [rowId]: err instanceof Error ? err.message : 'An error occurred while fetching combo data' }));
            console.error('Error fetching combo data:', err);
        } finally {
            if (comboAbortControllersRef.current[rowId] === requestController) {
                comboAbortControllersRef.current[rowId] = null;
                if (activeComboRequestKeysRef.current[rowId] === requestKey) {
                    activeComboRequestKeysRef.current[rowId] = null;
                }
            }

            if (requestId === latestComboRequestIdsRef.current[rowId]) {
                setComboLoading(prev => ({ ...prev, [rowId]: false }));
            }
        }
    }, [selectedDC]);

    const fetchViewPayloadData = useCallback(async (rowId: number, routeId: string) => {
        setViewPayloadLoading(true);
        setViewPayloadError(null);
        setViewPayloadData(null); // Clear previous data
        setJsonSearchTerm(''); // Clear search term
        setCurrentRouteId(routeId);

        try {
            const dcName = selectedDC;
            const data: RoutePayload = await fetchViewPayload(rowId, dcName);
            setViewPayloadData(data);
            setViewPayloadDialogOpen(true);
        } catch (err) {
            setViewPayloadError(err instanceof Error ? err.message : 'An error occurred while fetching view payload data');
            console.error('Error fetching view payload data:', err);
        } finally {
            setViewPayloadLoading(false);
        }
    }, [selectedDC]);

    const fetchReplayData = useCallback(async (routeId: number) => {
        
        setReplayLoading(true);
        setReplayError(null);
        try {
            const dcName = selectedDC;
            const { userId } = authProvider();
            if (!userId) {
                throw new Error('User authentication required for replay functionality');
            }

            const data = await fetchReplay(routeId, dcName, userId);
            setReplayData(data);
        } catch (error) {
            setReplayError(error instanceof Error ? error.message : 'Failed to replay route');
        } finally {
            setReplayLoading(false);
        }
    }, [selectedDC]);

    // Event handlers
    const handleChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    }, []);

    const handleFilterChange = useCallback((column: string, value: string) => {
        setFilters(prev => {
            if ((prev[column] ?? '') === value) {
                return prev;
            }

            return {
                ...prev,
                [column]: value
            };
        });
        setPage(0);
    }, []);

    const handleDateFilterChange = useCallback((type: 'from' | 'to', value: string) => {
        setDateFilters(prev => ({
            ...prev,
            [type]: value
        }));
        setPage(0);
    }, []);

    const handleClearDateFilters = useCallback(() => {
        setDateFilters({ from: '', to: '' });
        setPage(0);
    }, []);

    const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to first page when changing page size
    }, []);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);


    const handleToggleExpand = useCallback((rowId: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    }, []);

    const previousExpandedRowsRef = useRef<Set<number>>(new Set());

    useEffect(() => {
        const previousExpandedRows = previousExpandedRowsRef.current;
        const newlyExpandedRows = Array.from(expandedRows).filter((rowId) => !previousExpandedRows.has(rowId));

        previousExpandedRowsRef.current = new Set(expandedRows);

        if (!apiData || newlyExpandedRows.length === 0) {
            return;
        }

        newlyExpandedRows.forEach((rowId) => {
            const routeData = apiData.content.find(route => route.id === rowId);
            if (!routeData) {
                return;
            }

            const isSplit = isRouteFlagEnabled(routeData.splitFlag);
            void fetchComboData(rowId, routeData.createTs, isSplit, routeData.routeId);
        });
    }, [expandedRows, apiData, fetchComboData]);

    // Computed values
    const filteredAndSortedData = useMemo(() => {
        let data = apiData?.content || [];

        if (dateFilters.from || dateFilters.to) {
            data = data.filter(row => {
                const rowDate = new Date(row.createTs);

                if (dateFilters.from) {
                    const fromDate = new Date(dateFilters.from);
                    if (rowDate < fromDate) return false;
                }

                if (dateFilters.to) {
                    const toDate = new Date(dateFilters.to);
                    toDate.setHours(23, 59, 59, 999);
                    if (rowDate > toDate) return false;
                }

                return true;
            });
        }

        return data;
    }, [apiData, dateFilters]);

    const paginatedData = useMemo(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredAndSortedData.slice(startIndex, endIndex);
    }, [filteredAndSortedData, page, rowsPerPage]);

    const filteredAndSortedSplitWorksheetData = useMemo(() => {
        let filtered = splitWorksheetApiData.filter(row => {
            return Object.entries(splitWorksheetFilters).every(([key, value]) => {
                if (!value || !value.trim()) return true;
                const cellValue = row[key as keyof SplitWorksheetData];
                const stringValue = String(cellValue || '').toLowerCase().trim();
                const filterValue = value.toLowerCase().trim();
                // Check if the filter value appears as a consecutive substring
                return stringValue.includes(filterValue);
            });
        });

        return filtered;
    }, [splitWorksheetApiData, splitWorksheetFilters]);

    const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
    const totalElements = filteredAndSortedData.length;
    const startEntry = page * rowsPerPage + 1;
    const endEntry = Math.min((page + 1) * rowsPerPage, totalElements);

    // Effects
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            void fetchRouteData();
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [fetchRouteData]);

    useEffect(() => {
        const comboAbortControllers = comboAbortControllersRef.current;

        return () => {
            routeAbortControllerRef.current?.abort();
            splitAbortControllerRef.current?.abort();
            Object.values(comboAbortControllers).forEach((controller) => {
                controller?.abort();
            });
        };
    }, []);

    useEffect(() => {
        if (page >= totalPages && totalPages > 0) {
            setPage(0);
        }
    }, [page, totalPages]);

    return {
        // State
        value,
        setValue,
        apiData,
        loading,
        error,
        splitWorksheetApiData,
        splitWorksheetLoading,
        splitWorksheetError,
        comboData,
        comboLoading,
        comboError,
        viewPayloadData,
        viewPayloadLoading,
        viewPayloadError,
        viewPayloadDialogOpen,
        setViewPayloadDialogOpen,
        currentRouteId,
        setCurrentRouteId,
        jsonSearchTerm,
        setJsonSearchTerm,
        expandedPayloadRows,
        setExpandedPayloadRows,
        replayData,
        replayLoading,
        replayError,
        replayConfirmDialogOpen,
        setReplayConfirmDialogOpen,
        pendingReplayRouteId,
        setPendingReplayRouteId,
        filters,
        setFilters,
        dateFilters,
        setDateFilters,
        splitWorksheetFilters,
        setSplitWorksheetFilters,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        expandedRows,
        setExpandedRows,

        // Computed values
        filteredAndSortedData,
        paginatedData,
        filteredAndSortedSplitWorksheetData,
        totalPages,
        totalElements,
        startEntry,
        endEntry,

        // Handlers
        handleChange,
        handleFilterChange,
        handleDateFilterChange,
        handleClearDateFilters,
        handleRowsPerPageChange,
        handlePageChange,
        handleToggleExpand,

        // API functions
        fetchRouteData,
        fetchSplitWorksheetData,
        fetchComboData,
        fetchViewPayloadData,
        fetchReplayData
    };
};